import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"
import _imports_0 from '@/assets/logo.png'


const _hoisted_1 = { class: "navbar-scaler" }
const _hoisted_2 = { class: "nav-left" }
const _hoisted_3 = { class: "input-wrapper" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = { class: "nav-center" }
const _hoisted_6 = { class: "nav-links" }
const _hoisted_7 = { class: "nav-right" }
const _hoisted_8 = { class: "lang-switch" }

import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { Search } from 'lucide-vue-next';

// --- 属性定义 ---

export default /*@__PURE__*/_defineComponent({
  __name: 'navbar',
  props: {
  // 因某些页面（如批量采购）背景为白色，需要强制导航栏显示为“已滚动”状态（黑字）
  forceScrolled: {
    type: Boolean,
    default: false
  }
},
  setup(__props) {

/**
 * Navbar（响应式顶栏）
 * 
 * 功能概述：
 * 1. 布局：左侧 Logo + 导航链接，右侧登录/注册。
 * 2. 交互：
 *    - 滚动变色：页面滚动超过 50px 后，背景变白半透明，文字变深。
 *    // 因用户反馈，去除了停止操作 10 秒后自动上滑隐藏的功能
 *    - 下拉菜单：“产品”与“订阅”支持悬停展开，点击外部自动关闭。
 *    - 平滑滚动：点击“经销商”平滑滚动至底部地图。
 * 3. 适配：
 *    - 使用 clamp() 实现响应式字体与间距。
 *    - (已移除) 引入缩放机制（Scaler）：在窄屏下不再整体缩放，而是保证字体和Logo大小。
 */

const props = __props;

// --- 状态与引用 ---
const route = useRoute();
const router = useRouter();
const isScrolled = ref(false); // 是否已滚动（控制背景色）
const isSearchOpen = ref(false);
const searchKeyword = ref(''); 

// DOM 引用（用于点击外部检测）
const searchRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);

// --- 计算属性 ---
// 当前语言状态 (根据路由前缀判断)
const isEn = computed(() => route.path.startsWith('/eng'));

// 目标语言路径计算
const targetEnPath = computed(() => {
  const path = route.path;
  if (!path.startsWith('/eng')) {
    if (path === '/') return '/eng';
    return `/eng${path}`;
  }
  return path;
});

// 是否为触摸设备 (仅在挂载后检测)
const isTouchDevice = ref(false);

// --- 核心逻辑：滚动与窗口适配 ---

/**
 * 滚动事件处理
 * 更新滚动状态
 * // 因用户需求，去除了自动隐藏导航栏的计时逻辑
 */
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

// // --- 交互逻辑：下拉菜单 ---

// // 通用开关切换
// const toggleShop = () => isShopOpen.value = !isShopOpen.value;
// const onEnterShop = () => isShopOpen.value = true;
// const onLeaveShop = () => isShopOpen.value = false;

// // 触摸设备上的点击处理（防止点击直接跳转）
// const handleTouchClick = (e: MouseEvent, stateRef: typeof isShopOpen) => {
//   if (isTouchDevice.value) {
//     e.preventDefault();
//     stateRef.value = !stateRef.value;
//   }
// };

// const onShopTriggerClick = (e: MouseEvent) => handleTouchClick(e, isShopOpen);

// 点击文档外部关闭菜单
const onDocClick = (e: MouseEvent) => {
  const target = e.target as Node;
  if (searchRef.value && !searchRef.value.contains(target)) isSearchOpen.value = false;
};

// 搜索框交互逻辑
const handleSearchClick = () => {
  if (!isSearchOpen.value) {
    isSearchOpen.value = true;
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  } else {
    handleSearch();
  }
};

const handleSearch = () => {
  if (searchKeyword.value.trim()) {
    const url = `https://b.zimimodel.com/web/#/pages/goods-search/goods-search?keywords=${encodeURIComponent(searchKeyword.value.trim())}`;
    window.open(url, '_blank');
    isSearchOpen.value = false;
    searchKeyword.value = '';
  } else {
    isSearchOpen.value = false;
  }
};

// --- 业务逻辑：订阅与导航 ---

/**
 * 滚动到经销商地图区域
 */
const scrollToDistributor = async (e: MouseEvent) => {
  e.preventDefault();
  
  // 如果不在首页，先跳转到首页
  if (route.path !== '/') {
    await router.push('/');
    // 等待 DOM 更新
    setTimeout(() => {
      const el = document.getElementById('distributor-map');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return;
  }

  const el = document.getElementById('distributor-map');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  document.addEventListener('click', onDocClick, { passive: true });
  
  // 初始状态检查
  handleScroll();
  
  // 检测触摸设备
  isTouchDevice.value = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || ('ontouchstart' in window);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', onDocClick);
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("nav", {
    class: _normalizeClass(["navbar", { 
      'navbar-scrolled': isScrolled.value || props.forceScrolled
    }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "logo" }, [
          _createElementVNode("a", {
            href: "https://zimimodel.com",
            style: {"display":"block"}
          }, [
            _createElementVNode("img", {
              src: _imports_0,
              alt: "Logo"
            })
          ])
        ], -1)),
        _createElementVNode("div", {
          class: _normalizeClass(["nav-search", { open: isSearchOpen.value }]),
          ref_key: "searchRef",
          ref: searchRef
        }, [
          _createElementVNode("div", _hoisted_3, [
            _withDirectives(_createElementVNode("input", {
              ref_key: "searchInputRef",
              ref: searchInputRef,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchKeyword).value = $event)),
              type: "text",
              placeholder: isEn.value ? 'Search...' : '搜索...',
              onKeyup: _withKeys(handleSearch, ["enter"])
            }, null, 40, _hoisted_4), [
              [_vModelText, searchKeyword.value]
            ])
          ]),
          _createElementVNode("div", {
            class: "icon-wrapper",
            onClick: handleSearchClick
          }, [
            _createVNode(_unref(Search), { class: "search-icon" })
          ])
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _cache[4] || (_cache[4] = _createElementVNode("a", {
            href: "https://b.zimimodel.com/",
            target: "_blank",
            rel: "noopener"
          }, "商城", -1)),
          _createVNode(_component_router_link, {
            to: "/socials",
            target: "_blank",
            rel: "noopener"
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode("资讯", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_router_link, { to: "/contact" }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("联系", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_component_router_link, {
            to: _unref(route).path,
            class: _normalizeClass(["lang-opt", { active: !isEn.value }])
          }, {
            default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
              _createTextVNode("CN", -1)
            ]))]),
            _: 1
          }, 8, ["to", "class"]),
          _createVNode(_component_router_link, {
            to: targetEnPath.value,
            class: _normalizeClass(["lang-opt", { active: isEn.value }])
          }, {
            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createTextVNode("EN", -1)
            ]))]),
            _: 1
          }, 8, ["to", "class"])
        ]),
        _cache[7] || (_cache[7] = _createElementVNode("a", {
          class: "user-center-link",
          href: "https://b.zimimodel.com/web/#/pages/login/login",
          "aria-label": "用户中心"
        }, [
          _createElementVNode("img", {
            src: "https://cdn.cnbj1.fds.api.mi-img.com/staticsfile/visual/user.png",
            alt: "用户中心"
          })
        ], -1))
      ])
    ])
  ], 2))
}
}

})